import React from "react";
import { Link } from "react-router-dom";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12 pt-24 pb-20">
        <Link to="/" className="inline-flex items-center gap-2 text-xs text-[#52525B] hover:text-[#0A0A0A] mb-8 transition-colors">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 3L5 7L9 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          Back to home
        </Link>

        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter text-[#0A0A0A] mb-8">
          Terms & Conditions
        </h1>
        <p className="text-sm text-[#52525B] mb-10">Last updated: 2026-06-01</p>

        <div className="space-y-10 text-sm text-[#0A0A0A] leading-relaxed">
          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">1. Introduction and Acceptance</h2>
            <p className="text-[#52525B] mb-3">
              Welcome to KnowVex ("we," "us," or "our"). These Terms and Conditions ("Terms") govern your access to and use of the KnowVex platform and all associated services, tools, and features (collectively, the "Services") available at knowvex.org and related domains.
            </p>
            <p className="text-[#52525B] mb-3">
              By accessing or using the Services, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Services.
            </p>
            <p className="text-[#52525B]">
              We reserve the right to update, change, or replace any part of these Terms at any time. We will notify you of material changes by posting the updated Terms on our website with a new "Last Updated" date. Your continued use of the Services after such changes constitutes your acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">2. Definitions</h2>
            <ul className="space-y-2 text-[#52525B] list-disc pl-5">
              <li><strong className="text-[#0A0A0A]">"Services"</strong> means all products, tools, features, and services offered by KnowVex, including but not limited to web applications, APIs, mobile applications, and related technologies.</li>
              <li><strong className="text-[#0A0A0A]">"Platform"</strong> refers to the KnowVex technology infrastructure that powers the Services.</li>
              <li><strong className="text-[#0A0A0A]">"User"</strong> or <strong className="text-[#0A0A0A]">"you"</strong> means any individual or entity that accesses or uses the Services.</li>
              <li><strong className="text-[#0A0A0A]">"Content"</strong> means any data, text, files, information, usernames, images, graphics, photos, profiles, audio, video, items, or materials that you or other users upload, submit, post, transmit, or otherwise make available through the Services.</li>
              <li><strong className="text-[#0A0A0A]">"Account"</strong> means the user account created when you register for the Services.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">3. Account Registration and Security</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">3.1 Account Creation</h3>
            <p className="text-[#52525B] mb-3">
              To access certain features of the Services, you must create an Account. You may register using supported authentication providers (such as Google OAuth) or other methods we make available.
            </p>
            <p className="text-[#52525B] mb-2">You agree to:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your Account information</li>
              <li>Maintain the security of your Account credentials</li>
              <li>Accept responsibility for all activities that occur under your Account</li>
              <li>Notify us immediately of any unauthorized use of your Account</li>
            </ul>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">3.2 Account Eligibility</h3>
            <p className="text-[#52525B] mb-3">
              You represent and warrant that you have the legal capacity to enter into these Terms. If you are accessing the Services on behalf of an organization, you represent that you have authority to bind that organization to these Terms.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">3.3 Account Termination</h3>
            <p className="text-[#52525B]">
              We reserve the right to suspend or terminate your Account at any time, with or without cause, and with or without notice. You may also delete your Account at any time through the account settings or by contacting us at support@knowvex.org.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">4. Use of Services</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">4.1 License Grant</h3>
            <p className="text-[#52525B] mb-3">
              Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for their intended purpose.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">4.2 Acceptable Use</h3>
            <p className="text-[#52525B] mb-2">You agree to use the Services only for lawful purposes and in accordance with these Terms. You agree not to:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Violate any applicable laws, regulations, or third-party rights</li>
              <li>Use the Services to harm, harass, threaten, or discriminate against others</li>
              <li>Upload or transmit viruses, malware, or other malicious code</li>
              <li>Attempt to gain unauthorized access to our systems or networks</li>
              <li>Interfere with or disrupt the integrity or performance of the Services</li>
              <li>Use automated tools (bots, scrapers, crawlers) without our prior written permission</li>
              <li>Reverse engineer, decompile, or disassemble any part of the Services</li>
              <li>Remove, obscure, or alter any proprietary notices on the Services</li>
              <li>Use the Services to compete with us or develop competing products</li>
              <li>Sublicense, sell, resell, or commercialize the Services without authorization</li>
            </ul>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">4.3 Service-Specific Usage</h3>
            <p className="text-[#52525B]">
              Different services within the KnowVex platform may have specific usage guidelines, features, or limitations. You agree to comply with any service-specific terms or documentation provided.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">5. User Content and Data</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">5.1 Ownership</h3>
            <p className="text-[#52525B] mb-3">
              You retain ownership of all Content you upload, submit, or transmit through the Services. We do not claim ownership of your Content.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">5.2 License to KnowVex</h3>
            <p className="text-[#52525B] mb-2">
              By submitting Content to the Services, you grant us a worldwide, non-exclusive, royalty-free, transferable license to use, reproduce, modify, adapt, publish, translate, distribute, and display such Content solely for the purpose of:
            </p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Operating, maintaining, and improving the Services</li>
              <li>Providing the Services to you and other users</li>
              <li>Developing new features and services</li>
              <li>Complying with legal obligations</li>
            </ul>
            <p className="text-[#52525B] mb-3">
              This license terminates when you delete your Content or Account, except where Content has been shared with other users or where retention is required by law.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">5.3 Data Privacy</h3>
            <p className="text-[#52525B] mb-3">
              Your privacy is important to us. Please review our Privacy Policy to understand how we collect, use, and protect your personal information.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">5.4 User Responsibility</h3>
            <p className="text-[#52525B] mb-3">
              You are solely responsible for your Content and the consequences of posting or publishing it. You represent and warrant that you have all necessary rights to submit your Content and grant the licenses described in these Terms.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">5.5 Prohibited Content</h3>
            <p className="text-[#52525B] mb-2">You may not upload, post, or transmit Content that:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5">
              <li>Infringes intellectual property rights or other proprietary rights</li>
              <li>Contains personal information of others without consent</li>
              <li>Is illegal, fraudulent, defamatory, obscene, or offensive</li>
              <li>Contains malware, viruses, or harmful code</li>
              <li>Violates any applicable law or regulation</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">6. Intellectual Property Rights</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">6.1 Platform Ownership</h3>
            <p className="text-[#52525B] mb-3">
              The Services, including all software, algorithms, designs, text, graphics, logos, icons, images, audio clips, and other materials (excluding User Content), are owned by KnowVex or our licensors and are protected by copyright, trademark, and other intellectual property laws.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">6.2 Trademarks</h3>
            <p className="text-[#52525B] mb-3">
              KnowVex, our logos, and other marks are trademarks of KnowVex. You may not use our trademarks without our prior written permission.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">6.3 AI-Generated Content</h3>
            <p className="text-[#52525B] mb-2">
              Some Services may use artificial intelligence to generate content, recommendations, or outputs based on user inputs. The ownership and licensing of AI-generated content will be governed by the specific service terms and these Terms. Generally:
            </p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Inputs you provide remain your property</li>
              <li>AI-generated outputs based on your inputs are licensed to you for your use</li>
              <li>We reserve the right to use de-identified, aggregated data to improve our AI models</li>
            </ul>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">6.4 Feedback</h3>
            <p className="text-[#52525B]">
              If you provide feedback, suggestions, or ideas about the Services ("Feedback"), you grant us the right to use such Feedback without restriction or compensation to you.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">7. Third-Party Services and Integrations</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">7.1 Third-Party Providers</h3>
            <p className="text-[#52525B] mb-2">
              The Services may rely on or integrate with third-party service providers, including but not limited to:
            </p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Cloud infrastructure providers (e.g., AWS, Google Cloud)</li>
              <li>Authentication services (e.g., Google OAuth)</li>
              <li>Artificial intelligence services (e.g., Google Gemini, OpenAI)</li>
              <li>Database services (e.g., MongoDB Atlas)</li>
              <li>Storage services (e.g., Amazon S3)</li>
            </ul>
            <p className="text-[#52525B] mb-3">
              Your use of these third-party services is subject to their respective terms of service and privacy policies.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">7.2 Third-Party Links</h3>
            <p className="text-[#52525B] mb-3">
              The Services may contain links to third-party websites or services. We are not responsible for the content, accuracy, or practices of third-party sites. Your use of third-party sites is at your own risk.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">7.3 No Endorsement</h3>
            <p className="text-[#52525B]">
              We do not endorse, warrant, or assume responsibility for any third-party services, products, or content accessed through the Services.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">8. Fees and Payment</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">8.1 Pricing</h3>
            <p className="text-[#52525B] mb-3">
              Certain Services or features may be offered on a paid basis. Pricing details will be displayed within the Services or on our website. We reserve the right to change our pricing at any time, with notice to existing paying users.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">8.2 Payment Terms</h3>
            <p className="text-[#52525B] mb-2">If you purchase paid Services:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>You agree to pay all fees according to the pricing and payment terms in effect at the time</li>
              <li>Payments are processed through third-party payment processors</li>
              <li>All fees are non-refundable unless otherwise stated or required by law</li>
              <li>You authorize us to charge your selected payment method for all applicable fees</li>
            </ul>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">8.3 Subscription Services</h3>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Subscriptions automatically renew unless you cancel before the renewal date</li>
              <li>We will charge your payment method at the start of each billing cycle</li>
              <li>You may cancel your subscription at any time through account settings</li>
              <li>Cancellations take effect at the end of the current billing period</li>
            </ul>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">8.4 Taxes</h3>
            <p className="text-[#52525B] mb-3">
              All fees are exclusive of applicable taxes. You are responsible for paying all taxes associated with your use of the Services.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">8.5 Free Trials and Promotions</h3>
            <p className="text-[#52525B]">
              We may offer free trials or promotional pricing. These offers are subject to specific terms and limitations. We reserve the right to modify or discontinue promotions at any time.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">9. Service Availability and Support</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">9.1 Availability</h3>
            <p className="text-[#52525B] mb-2">
              We strive to make the Services available 24/7, but we do not guarantee uninterrupted or error-free access. The Services may be unavailable due to:
            </p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>Scheduled or emergency maintenance</li>
              <li>Technical issues beyond our control</li>
              <li>Force majeure events</li>
              <li>Service updates or modifications</li>
            </ul>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">9.2 Modifications</h3>
            <p className="text-[#52525B] mb-3">
              We reserve the right to modify, suspend, or discontinue any part of the Services at any time, with or without notice. We are not liable for any modification, suspension, or discontinuation of the Services.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">9.3 Beta Features</h3>
            <p className="text-[#52525B] mb-3">
              We may offer beta, pilot, or experimental features. These features are provided "as-is" without warranties and may be modified or discontinued at any time.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">9.4 Support</h3>
            <p className="text-[#52525B]">
              Support options, if any, will be described in service-specific documentation or on our website. We do not guarantee specific response times unless stated in a separate support agreement.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">10. Data Retention and Account Deletion</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">10.1 Data Retention</h3>
            <p className="text-[#52525B] mb-3">
              We retain your data as long as your Account is active or as needed to provide the Services. Retention periods may vary based on data type and legal requirements.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">10.2 Account Deletion</h3>
            <p className="text-[#52525B] mb-2">When you delete your Account:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>We will delete or anonymize your personal information within a reasonable timeframe</li>
              <li>Some data may be retained for legal, security, or operational purposes</li>
              <li>Content shared with other users may remain available to those users</li>
              <li>Backups may be retained for a limited period</li>
            </ul>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">10.3 Data Export</h3>
            <p className="text-[#52525B]">
              You may request an export of your data at any time through account settings or by contacting us at support@knowvex.org.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">11. Disclaimers</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">11.1 "As-Is" Service</h3>
            <p className="text-[#52525B] mb-3 uppercase tracking-wide text-xs font-semibold">
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">11.2 No Guarantees</h3>
            <p className="text-[#52525B] mb-2">We do not warrant that:</p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-4">
              <li>The Services will meet your specific requirements</li>
              <li>The Services will be uninterrupted, timely, secure, or error-free</li>
              <li>Results obtained from the Services will be accurate or reliable</li>
              <li>Any errors in the Services will be corrected</li>
              <li>AI-generated content or recommendations will be accurate, complete, or suitable for your purposes</li>
            </ul>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">11.3 Use at Your Own Risk</h3>
            <p className="text-[#52525B]">
              You acknowledge that your use of the Services is at your sole risk. We are not responsible for decisions you make based on Content, AI-generated outputs, or information obtained through the Services.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">12. Limitation of Liability</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">12.1 Limitation of Damages</h3>
            <p className="text-[#52525B] mb-3 uppercase tracking-wide text-xs font-semibold">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, KNOWVEX AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, LICENSORS, AND SERVICE PROVIDERS SHALL NOT BE LIABLE FOR: INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES; LOSS OF PROFITS, REVENUE, DATA, OR BUSINESS OPPORTUNITIES; LOSS OF GOODWILL OR REPUTATION; SERVICE INTERRUPTIONS OR DATA LOSS. ARISING OUT OF OR RELATED TO THESE TERMS OR YOUR USE OF THE SERVICES, REGARDLESS OF THE THEORY OF LIABILITY AND EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">12.2 Liability Cap</h3>
            <p className="text-[#52525B] mb-3 uppercase tracking-wide text-xs font-semibold">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICES SHALL NOT EXCEED THE GREATER OF: THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRIOR TO THE EVENT GIVING RISE TO LIABILITY, OR ONE HUNDRED U.S. DOLLARS (USD $100).
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">12.3 Exceptions</h3>
            <p className="text-[#52525B]">
              Some jurisdictions do not allow the exclusion or limitation of certain warranties or damages. In such jurisdictions, our liability will be limited to the maximum extent permitted by law.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">13. Indemnification</h2>
            <p className="text-[#52525B] mb-2">
              You agree to indemnify, defend, and hold harmless KnowVex and its officers, directors, employees, agents, licensors, and service providers from and against any claims, liabilities, damages, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or related to:
            </p>
            <ul className="space-y-1 text-[#52525B] list-disc pl-5 mb-3">
              <li>Your violation of these Terms</li>
              <li>Your violation of any law or regulation</li>
              <li>Your violation of any third-party rights, including intellectual property rights</li>
              <li>Your Content or use of the Services</li>
              <li>Your negligence or willful misconduct</li>
            </ul>
            <p className="text-[#52525B]">
              We reserve the right to assume exclusive defense and control of any matter subject to indemnification by you, at your expense.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">14. Dispute Resolution</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">14.1 Governing Law</h3>
            <p className="text-[#52525B] mb-3">
              These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">14.2 Arbitration</h3>
            <p className="text-[#52525B] mb-3">
              Any dispute, claim, or controversy arising out of or relating to these Terms or the Services shall be resolved through binding arbitration, except that either party may seek injunctive or other equitable relief in court to protect intellectual property rights. The arbitration shall be conducted by a single arbitrator in accordance with the rules of a recognized arbitration organization. The arbitrator's decision shall be final and binding.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">14.3 Class Action Waiver</h3>
            <p className="text-[#52525B] mb-3">
              You agree that any arbitration or legal proceeding shall be conducted on an individual basis and not as a class action, consolidated action, or representative action. You waive any right to participate in a class action lawsuit or class-wide arbitration.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">14.4 Informal Resolution</h3>
            <p className="text-[#52525B]">
              Before filing an arbitration claim, you agree to first contact us at support@knowvex.org to attempt to resolve the dispute informally. We will attempt to resolve disputes in good faith for at least thirty (30) days.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">15. General Provisions</h2>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">15.1 Entire Agreement</h3>
            <p className="text-[#52525B] mb-3">
              These Terms, together with our Privacy Policy and any service-specific terms, constitute the entire agreement between you and KnowVex regarding the Services and supersede all prior agreements and understandings.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">15.2 Severability</h3>
            <p className="text-[#52525B] mb-3">
              If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">15.3 Waiver</h3>
            <p className="text-[#52525B] mb-3">
              Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">15.4 Assignment</h3>
            <p className="text-[#52525B] mb-3">
              You may not assign or transfer these Terms or your rights under these Terms without our prior written consent. We may assign these Terms without restriction.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">15.5 Force Majeure</h3>
            <p className="text-[#52525B] mb-3">
              We shall not be liable for any failure or delay in performing our obligations under these Terms due to circumstances beyond our reasonable control, including acts of God, war, terrorism, labor disputes, or internet service provider failures.
            </p>
            <h3 className="font-heading font-semibold text-base text-[#0A0A0A] mb-2">15.6 Survival</h3>
            <p className="text-[#52525B]">
              Sections that by their nature should survive termination of these Terms shall survive, including but not limited to Sections 5 (User Content), 6 (Intellectual Property), 11 (Disclaimers), 12 (Limitation of Liability), 13 (Indemnification), and 14 (Dispute Resolution).
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">16. Contact Information</h2>
            <p className="text-[#52525B] mb-2">
              If you have any questions about these Terms, please contact us:
            </p>
            <div className="text-[#52525B] border border-black/10 p-4">
              <p className="font-semibold text-[#0A0A0A]">KnowVex</p>
              <p>Email: support@knowvex.org</p>
              <p>Address: D-111, Sector 26, Noida, Uttar Pradesh, India 201301</p>
            </div>
            <p className="text-[#52525B] mt-3 text-xs italic">
              By using the Services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
          </section>
        </div>
      </div>

      <footer className="border-t border-black/10 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo_vector.svg" alt="Quick Assess" className="w-8 h-8" />
            <div className="flex flex-col leading-tight">
              <span className="text-base text-[#52525B]">Quick <span className="text-[#FF3366]">Assess</span></span>
              <span className="text-xs text-[#52525B]">by KnowVex</span>
            </div>
          </div>
          <p className="text-xs text-[#52525B]">AI-powered technical assessments</p>
        </div>
      </footer>
    </div>
  );
}
